import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token + Web Session id to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Forward the website-analytics session id so backend can attribute conversions
  try {
    const wa_sid = sessionStorage.getItem('wa_sid');
    if (wa_sid) {
      config.headers['X-Web-Session-Id'] = wa_sid;
    }
  } catch (_e) { /* sessionStorage unavailable — skip */ }
  return config;
});

// Handle auth errors - only redirect to admin for admin-specific routes
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      // Only redirect to admin login for admin-specific routes
      if (requestUrl.includes('/admin/') || requestUrl.includes('/auth/login')) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        // Only redirect if we're on an admin page
        if (window.location.pathname.startsWith('/admin')) {
          window.location.href = '/admin';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Use Cases API
export const useCasesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/usecases', { params });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/usecases/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/usecases', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/usecases/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/usecases/${id}`);
    return response.data;
  },
  vote: async (id) => {
    const response = await api.post(`/usecases/${id}/vote`);
    return response.data;
  },
  unvote: async (id) => {
    const response = await api.post(`/usecases/${id}/vote?action=unvote`);
    return response.data;
  },
  getCount: async () => {
    const response = await api.get('/usecases/count');
    return response.data;
  },
};

// Assessments API
export const assessmentsAPI = {
  create: async (data) => {
    const response = await api.post('/assessments', data);
    return response.data;
  },
  getAll: async (params = {}) => {
    const response = await api.get('/assessments', { params });
    return response.data;
  },
  getAllWithAnswers: async (params = {}) => {
    const response = await api.get('/assessments', { params: { ...params, include_answers: true } });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/assessments/${id}`);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/assessments/${id}`);
    return response.data;
  },
  updateLeadStatus: async (id, leadStatus) => {
    const response = await api.patch(`/assessments/${id}/lead-status?lead_status=${leadStatus}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/assessments/stats');
    return response.data;
  },
  getPublicStats: async () => {
    const response = await api.get('/assessments/public-stats');
    return response.data;
  },
  getBenchmark: async () => {
    const response = await api.get('/assessments/benchmark');
    return response.data;
  },
  getMyAssessments: async () => {
    const response = await api.get('/assessments/my-assessments');
    return response.data;
  },
  // Update assessment data (admin)
  update: async (assessmentId, data) => {
    const response = await api.put(`/assessments/${assessmentId}`, data);
    return response.data;
  },
  // Delete assessment (admin)
  delete: async (assessmentId) => {
    const response = await api.delete(`/assessments/${assessmentId}`);
    return response.data;
  },
};

// User Stats API
export const userStatsAPI = {
  getMyStats: async () => {
    const response = await api.get('/users/me/stats');
    return response.data;
  },
};

// Admin Stats and Operations
export const adminAPI = {
  seedDatabase: async () => {
    const response = await api.post('/admin/seed');
    return response.data;
  },
  seedExtraUsecases: async () => {
    const response = await api.post('/admin/seed-extra-usecases');
    return response.data;
  },
  sendDigestTest: async () => {
    const response = await api.post('/admin/send-digest-test');
    return response.data;
  },
  uploadBrandingLogo: async (file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/admin/branding/logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  resetBrandingLogo: async () => {
    const response = await api.post('/admin/branding/reset');
    return response.data;
  },
  changeOwnPassword: async (currentPassword, newPassword) => {
    const response = await api.post('/admin/change-own-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },
  clearDatabase: async () => {
    const response = await api.delete('/admin/clear');
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
  getBackup: async () => {
    const response = await api.get('/admin/backup');
    return response.data;
  },
  getWorkshopBookings: async () => {
    const response = await api.get('/admin/workshop-bookings');
    return response.data;
  },
  updateBookingStatus: async (bookingId, status) => {
    const response = await api.put(`/admin/workshop-bookings/${bookingId}/status?status=${status}`);
    return response.data;
  },
  getSubmissions: async () => {
    const response = await api.get('/admin/submissions');
    return response.data;
  },
  updateSubmissionStatus: async (submissionId, status) => {
    const response = await api.put(`/admin/submissions/${submissionId}/status?status=${status}`);
    return response.data;
  },
  updateUseCaseGartner: async (usecaseId, data) => {
    const response = await api.put(`/admin/usecases/${usecaseId}/gartner`, data);
    return response.data;
  },
  getAllGartnerPositions: async () => {
    const response = await api.get('/admin/usecases/gartner-positions');
    return response.data;
  },
};

// HubSpot Integration API
export const hubspotAPI = {
  getStatus: async () => {
    const response = await api.get('/hubspot/status');
    return response.data;
  },
  createProperties: async () => {
    const response = await api.post('/hubspot/admin/create-properties');
    return response.data;
  },
  testSync: async () => {
    const response = await api.post('/hubspot/admin/test-sync');
    return response.data;
  },
};

// Beta Testing API
export const betaAPI = {
  register: async (name, email, inviteCode) => {
    const response = await api.post('/beta/register', { name, email, invite_code: inviteCode });
    return response.data;
  },
  login: async (email) => {
    const response = await api.post('/beta/login', { email });
    return response.data;
  },
  getMe: async (token) => {
    const response = await api.get(`/beta/me?token=${token}`);
    return response.data;
  },
  submitFeedback: async (token, feedbackData) => {
    const response = await api.post(`/beta/feedback?token=${token}`, feedbackData);
    return response.data;
  },
  submitFeedbackAsUser: async (feedbackData) => {
    const response = await api.post('/beta/feedback/user', feedbackData);
    return response.data;
  },
  getMyFeedback: async (token) => {
    const response = await api.get(`/beta/feedback?token=${token}`);
    return response.data;
  },
  getMyFeedbackByEmail: async (email) => {
    const response = await api.get(`/beta/feedback/by-email?email=${encodeURIComponent(email)}`);
    return response.data;
  },
  getAllFeedback: async () => {
    const response = await api.get('/beta/feedback/all');
    return response.data;
  },
  deleteFeedback: async (feedbackId) => {
    const response = await api.delete(`/beta/feedback/${feedbackId}`);
    return response.data;
  },
  updateFeedbackStatus: async (feedbackId, status) => {
    const response = await api.put(`/beta/feedback/${feedbackId}/status?new_status=${status}`);
    return response.data;
  },
  exportJSON: async () => {
    const response = await api.get('/beta/feedback/export/json');
    return response.data;
  },
  exportMarkdown: async () => {
    const response = await api.get('/beta/feedback/export/markdown');
    return response.data;
  },
  getTesters: async () => {
    const response = await api.get('/beta/testers');
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/beta/stats');
    return response.data;
  },
  
  // Admin: Invitation Code Management
  createInviteCode: async (data = {}) => {
    const response = await api.post('/beta/admin/codes', data);
    return response.data;
  },
  getInviteCodes: async () => {
    const response = await api.get('/beta/admin/codes');
    return response.data;
  },
  deleteInviteCode: async (codeId) => {
    const response = await api.delete(`/beta/admin/codes/${codeId}`);
    return response.data;
  },
  toggleInviteCode: async (codeId) => {
    const response = await api.put(`/beta/admin/codes/${codeId}/toggle`);
    return response.data;
  },
  
  // Admin: Beta Tester Management
  createTester: async (name, email) => {
    const response = await api.post('/beta/admin/testers', { name, email });
    return response.data;
  },
  deleteTester: async (testerId) => {
    const response = await api.delete(`/beta/admin/testers/${testerId}`);
    return response.data;
  },
  toggleTester: async (testerId) => {
    const response = await api.put(`/beta/admin/testers/${testerId}/toggle`);
    return response.data;
  },
  
  // Beta System Settings
  getSettings: async () => {
    const response = await api.get('/beta/settings');
    return response.data;
  },
  toggleBetaSystem: async () => {
    const response = await api.put('/beta/admin/settings/toggle');
    return response.data;
  },
  addAdminResponse: async (feedbackId, responseText) => {
    const response = await api.put(`/beta/admin/feedback/${feedbackId}/response`, { response: responseText });
    return response.data;
  },
  getMyFeedback: async (token) => {
    const response = await api.get(`/beta/my-feedback?token=${token}`);
    return response.data;
  },
  getTrends: async (days = 30) => {
    const response = await api.get(`/beta/stats/trends?days=${days}`);
    return response.data;
  },
};

// Saved Use Cases API
export const savedUseCasesAPI = {
  getSavedIds: async () => {
    const response = await api.get('/saved-usecases');
    return response.data;
  },
  getSavedWithDetails: async () => {
    const response = await api.get('/saved-usecases/details');
    return response.data;
  },
  save: async (usecaseId) => {
    const response = await api.post(`/saved-usecases/${usecaseId}`);
    return response.data;
  },
  unsave: async (usecaseId) => {
    const response = await api.delete(`/saved-usecases/${usecaseId}`);
    return response.data;
  },
  checkSaved: async (usecaseId) => {
    const response = await api.get(`/saved-usecases/check/${usecaseId}`);
    return response.data;
  },
  getSaveCount: async (usecaseId) => {
    const response = await api.get(`/saved-usecases/count/${usecaseId}`);
    return response.data;
  },
};

// Admin Users API
export const adminUsersAPI = {
  getAll: async (params = {}, config = {}) => {
    const response = await api.get('/admin/users', { params, ...config });
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/admin/users/stats');
    return response.data;
  },
  create: async (userData) => {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },
  getDetail: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },
  getUserStats: async (userId) => {
    const response = await api.get(`/admin/users/${userId}/stats`);
    return response.data;
  },
  updateUser: async (userId, data) => {
    const response = await api.put(`/admin/users/${userId}`, data);
    return response.data;
  },
  delete: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },
  toggleROIAccess: async (userId) => {
    const response = await api.put(`/admin/users/${userId}/roi-access`);
    return response.data;
  },
};

// Content API (testimonials, trusted companies, stats)
export const contentAPI = {
  getTestimonials: async () => {
    const response = await api.get('/content/testimonials');
    return response.data;
  },
  getTestimonialsAdmin: async () => {
    const response = await api.get('/content/testimonials/admin');
    return response.data;
  },
  createTestimonial: async (data) => {
    const response = await api.post('/content/testimonials', data);
    return response.data;
  },
  updateTestimonial: async (id, data) => {
    const response = await api.put(`/content/testimonials/${id}`, data);
    return response.data;
  },
  deleteTestimonial: async (id) => {
    const response = await api.delete(`/content/testimonials/${id}`);
    return response.data;
  },
  getTrustedCompanies: async () => {
    const response = await api.get('/content/trusted-companies');
    return response.data;
  },
  getTrustedCompaniesAdmin: async () => {
    const response = await api.get('/content/trusted-companies/admin');
    return response.data;
  },
  createTrustedCompany: async (data) => {
    const response = await api.post('/content/trusted-companies', data);
    return response.data;
  },
  updateTrustedCompany: async (id, data) => {
    const response = await api.put(`/content/trusted-companies/${id}`, data);
    return response.data;
  },
  deleteTrustedCompany: async (id) => {
    const response = await api.delete(`/content/trusted-companies/${id}`);
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/content/stats');
    return response.data;
  },
};

// PDF Generation API
export const pdfAPI = {
  generateFromAssessment: async (assessmentId) => {
    const response = await api.post(`/assessments/${assessmentId}/pdf`, {}, {
      responseType: 'blob',
    });
    return response.data;
  },
  generateFromData: async (data) => {
    const response = await api.post('/assessments/generate-pdf', data, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Workshop Bookings API
export const workshopAPI = {
  // Public
  createBooking: async (data) => {
    const response = await api.post('/workshops/book', data);
    return response.data;
  },
  
  // Admin
  getAllBookings: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/workshops/admin/all', { params });
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/workshops/admin/stats');
    return response.data;
  },
  updateBooking: async (id, data) => {
    const response = await api.put(`/workshops/admin/${id}`, data);
    return response.data;
  },
  deleteBooking: async (id) => {
    const response = await api.delete(`/workshops/admin/${id}`);
    return response.data;
  },
  createManualBooking: async (data) => {
    const response = await api.post('/workshops/admin/create', data);
    return response.data;
  },
};

// Subscription & Monetization API
export const subscriptionAPI = {
  // Public - Get available plans
  getPlans: async () => {
    const response = await api.get('/subscriptions/plans');
    return response.data;
  },
  getPlan: async (planId) => {
    const response = await api.get(`/subscriptions/plans/${planId}`);
    return response.data;
  },
  getFeatures: async () => {
    const response = await api.get('/subscriptions/features');
    return response.data;
  },
  
  // User - Get subscription status
  getMySubscription: async () => {
    const response = await api.get('/subscriptions/my-subscription');
    return response.data;
  },
  getMyFeatures: async () => {
    const response = await api.get('/subscriptions/my-features');
    return response.data;
  },
  checkFeatureAccess: async (featureId) => {
    const response = await api.post(`/subscriptions/check-feature/${featureId}`);
    return response.data;
  },
  
  // Usage tracking
  trackUsage: async (featureId) => {
    const response = await api.post(`/subscriptions/track-usage/${featureId}`);
    return response.data;
  },
  getMyUsage: async () => {
    const response = await api.get('/subscriptions/my-usage');
    return response.data;
  },
};

// Anonymous Session API - for tracking incomplete quickscans
export const sessionAPI = {
  // Public - Start and track sessions
  startSession: async (browserId = null) => {
    const response = await api.post('/sessions/start', { browser_id: browserId });
    return response.data;
  },
  updateProgress: async (sessionId, progressData) => {
    const response = await api.put(`/sessions/${sessionId}/progress`, progressData);
    return response.data;
  },
  completeSession: async (sessionId, assessmentId) => {
    const response = await api.post(`/sessions/${sessionId}/complete?assessment_id=${assessmentId}`);
    return response.data;
  },
  getSession: async (sessionId) => {
    const response = await api.get(`/sessions/${sessionId}`);
    return response.data;
  },
  
  // Admin - Session analytics
  getStats: async () => {
    const response = await api.get('/sessions/admin/stats');
    return response.data;
  },
  getAllSessions: async (statusFilter = null, limit = 50, skip = 0) => {
    const params = { limit, skip };
    if (statusFilter) params.status_filter = statusFilter;
    const response = await api.get('/sessions/admin/all', { params });
    return response.data;
  },
  getDropOffAnalysis: async () => {
    const response = await api.get('/sessions/admin/drop-off-analysis');
    return response.data;
  },
  getTimeline: async (days = 30) => {
    const response = await api.get('/sessions/admin/timeline', { params: { days } });
    return response.data;
  },
  deleteSession: async (sessionId) => {
    const response = await api.delete(`/sessions/admin/${sessionId}`);
    return response.data;
  },
  bulkDeleteSessions: async (sessionIds) => {
    const response = await api.delete('/sessions/admin/bulk', { data: sessionIds });
    return response.data;
  },
};

// Analytics API - Tracking and Admin Stats
export const analyticsAPI = {
  // Tracking endpoints
  trackView: async (data) => {
    try {
      const response = await api.post('/analytics/track/view', data);
      return response.data;
    } catch (error) {
      console.error('Failed to track view:', error);
      return null;
    }
  },
  trackViewEnd: async (viewId, timeSpentSeconds) => {
    try {
      const response = await api.post('/analytics/track/view/end', {
        view_id: viewId,
        time_spent_seconds: timeSpentSeconds
      });
      return response.data;
    } catch (error) {
      console.error('Failed to track view end:', error);
      return null;
    }
  },
  trackROICalculation: async (data) => {
    try {
      const response = await api.post('/analytics/track/roi-calculation', data);
      return response.data;
    } catch (error) {
      console.error('Failed to track ROI calculation:', error);
      return null;
    }
  },
  
  // Admin endpoints
  getUseCaseStats: async (period = 'all') => {
    const response = await api.get('/analytics/admin/use-case-stats', { params: { period } });
    return response.data;
  },
  getROIStats: async (period = 'all') => {
    const response = await api.get('/analytics/admin/roi-stats', { params: { period } });
    return response.data;
  },
  getTrends: async (days = 30) => {
    const response = await api.get('/analytics/admin/trends', { params: { days } });
    return response.data;
  },
  getRecentActivity: async (limit = 20) => {
    const response = await api.get('/analytics/admin/recent-activity', { params: { limit } });
    return response.data;
  },
};

// Comparison Analysis API
export const comparisonAPI = {
  // Generate AI analysis of assessment comparisons
  analyzeComparison: async (assessments) => {
    const response = await api.post('/comparison/analyze', { assessments });
    return response.data;
  },
  
  // Health check
  healthCheck: async () => {
    const response = await api.get('/comparison/health');
    return response.data;
  },
};

// Website Analytics API (admin)
export const websiteAnalyticsAPI = {
  overview: async (days = 30) => {
    const r = await api.get('/analytics/website/overview', { params: { days } });
    return r.data;
  },
  timeseries: async (days = 30) => {
    const r = await api.get('/analytics/website/timeseries', { params: { days } });
    return r.data;
  },
  topPages: async (days = 30, limit = 10) => {
    const r = await api.get('/analytics/website/top-pages', { params: { days, limit } });
    return r.data;
  },
  topReferrers: async (days = 30, limit = 10) => {
    const r = await api.get('/analytics/website/top-referrers', { params: { days, limit } });
    return r.data;
  },
  devices: async (days = 30) => {
    const r = await api.get('/analytics/website/devices', { params: { days } });
    return r.data;
  },
  funnel: async (days = 30) => {
    const r = await api.get('/analytics/funnel', { params: { days } });
    return r.data;
  },
  funnelBySource: async (days = 30, limit = 6) => {
    const r = await api.get('/analytics/funnel/by-source', { params: { days, limit } });
    return r.data;
  },
};

export const careersAPI = {
  submitApplication: async (data) => {
    const r = await api.post('/careers/applications', data);
    return r.data;
  },
  getApplications: async () => {
    const r = await api.get('/careers/admin/applications');
    return r.data;
  },
  deleteApplication: async (id) => {
    const r = await api.delete(`/careers/admin/applications/${id}`);
    return r.data;
  },
};

export const pricingAPI = {
  getSettings: async () => {
    const response = await api.get('/pricing/settings');
    return response.data;
  },
  updateSettings: async (payload) => {
    const response = await api.put('/pricing/settings', payload);
    return response.data;
  },
};

export const blogAPI = {
  getPosts: async () => {
    const r = await api.get('/blog/posts');
    return r.data;
  },
  trackViews: async (postIds) => {
    const r = await api.post('/blog/posts/view', { post_ids: postIds });
    return r.data;
  },
  getComments: async (postId) => {
    const r = await api.get(`/blog/posts/${postId}/comments`);
    return r.data;
  },
  addComment: async (postId, data) => {
    const r = await api.post(`/blog/posts/${postId}/comments`, data);
    return r.data;
  },
  adminGetPosts: async () => {
    const r = await api.get('/blog/admin/posts');
    return r.data;
  },
  adminGetComments: async () => {
    const r = await api.get('/blog/admin/comments');
    return r.data;
  },
  fetchLinkedInText: async (linkedinUrl) => {
    const r = await api.post('/blog/admin/fetch-linkedin-text', { linkedin_url: linkedinUrl });
    return r.data;
  },
  refreshPost: async (id) => {
    const r = await api.post(`/blog/admin/posts/${id}/refresh`);
    return r.data;
  },
  moderateComment: async (id, status) => {
    const r = await api.put(`/blog/admin/comments/${id}`, { status });
    return r.data;
  },
  deleteComment: async (id) => {
    const r = await api.delete(`/blog/admin/comments/${id}`);
    return r.data;
  },
  createPost: async (data) => {
    const r = await api.post('/blog/admin/posts', data);
    return r.data;
  },
  updatePost: async (id, data) => {
    const r = await api.put(`/blog/admin/posts/${id}`, data);
    return r.data;
  },
  deletePost: async (id) => {
    const r = await api.delete(`/blog/admin/posts/${id}`);
    return r.data;
  },
};

export default api;
